import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/constants';

const VendorsModule = ({ onBack }) => {
  const [selectedCity, setSelectedCity] = useState('');
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  
  // Location management state
  const [locations, setLocations] = useState([]);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [showLocationManager, setShowLocationManager] = useState(false);

  // Fetch locations from backend
  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/vendor-locations`);
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
      // Fallback to default cities if API fails
      setLocations([
        {id: '1', name: 'Thunder Bay', province: 'Ontario', region: 'Northwestern Ontario'},
        {id: '2', name: 'Sault Ste Marie', province: 'Ontario', region: 'Northern Ontario'},
        {id: '3', name: 'Sudbury', province: 'Ontario', region: 'Northern Ontario'},
        {id: '4', name: 'North Bay', province: 'Ontario', region: 'Northern Ontario'},
        {id: '5', name: 'Atikokan', province: 'Ontario', region: 'Northwestern Ontario'},
        {id: '6', name: 'Nipigon', province: 'Ontario', region: 'Northwestern Ontario'}
      ]);
    }
  };

  React.useEffect(() => {
    fetchLocations();
  }, []);

  // Get cities array from locations for backwards compatibility
  const cities = locations.map(loc => loc.name);

  // Seed vendor data for cities when component mounts
  React.useEffect(() => {
    const seedVendorData = async () => {
      try {
        // Seed Thunder Bay vendors
        await axios.post(`${API}/vendors/seed-thunder-bay`);
        console.log('Thunder Bay vendor data seeded successfully');
        
        // Seed Sudbury vendors
        await axios.post(`${API}/vendors/seed-sudbury`);
        console.log('Sudbury vendor data seeded successfully');
        
        // Seed North Bay vendors
        await axios.post(`${API}/vendors/seed-north-bay`);
        console.log('North Bay vendor data seeded successfully');
        
        // Seed Sault Ste Marie vendors
        await axios.post(`${API}/vendors/seed-sault-ste-marie`);
        console.log('Sault Ste Marie vendor data seeded successfully');
        
        // Seed Nipigon vendors
        await axios.post(`${API}/vendors/seed-nipigon`);
        console.log('Nipigon vendor data seeded successfully');
      } catch (error) {
        console.error('Error seeding vendor data:', error);
      }
    };
    seedVendorData();
  }, []);

  const handleCitySelect = async (city) => {
    setSelectedCity(city);
    setLoading(true);
    try {
      const response = await axios.get(`${API}/vendors/${city}`);
      setVendors(response.data);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      setVendors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditVendor = (vendor) => {
    setEditingVendor(vendor);
    setShowEditModal(true);
  };

  const handleDeleteVendor = async (vendorId, vendorName) => {
    if (window.confirm(`Are you sure you want to delete ${vendorName}?`)) {
      try {
        await axios.delete(`${API}/vendors/${vendorId}`);
        alert('Vendor deleted successfully!');
        handleCitySelect(selectedCity); // Refresh the list
      } catch (error) {
        console.error('Error deleting vendor:', error);
        alert('Error deleting vendor. Please try again.');
      }
    }
  };

  const handleBackToCity = () => {
    setSelectedCity('');
    setVendors([]);
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800">
      <div className="container mx-auto px-4 py-8">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button 
            onClick={selectedCity ? handleBackToCity : onBack} 
            className="text-blue-600 hover:text-blue-700 text-2xl"
          >
            ← Back
          </button>
          <h1 className="text-4xl font-bold text-gray-800">VENDOR LIST</h1>
          {selectedCity && (
            <button 
              onClick={() => setShowAddModal(true)}
              className="bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-xl font-bold text-white shadow-lg"
            >
              + ADD VENDOR
            </button>
          )}
        </div>

        {/* City Selection */}
        {!selectedCity && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-700">Select Your City</h2>
              <button
                onClick={() => setShowLocationManager(true)}
                className="bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded-lg font-bold text-white shadow-sm text-sm"
              >
                🗺️ Manage Locations
              </button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {cities.map((city) => (
                <button
                  key={city}
                  onClick={() => handleCitySelect(city)}
                  className="bg-white rounded-xl p-8 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl border border-gray-200"
                >
                  <div className="text-center">
                    <div className="text-4xl mb-4">🏙️</div>
                    <h3 className="text-xl font-bold text-gray-800">{city}</h3>
                    <p className="text-gray-500 text-sm mt-2">View suppliers & contractors</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Vendor List */}
        {selectedCity && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-800">{selectedCity}</h2>
                <p className="text-gray-600">Suppliers & Contractors</p>
              </div>
              <div className="text-blue-600 font-semibold">
                {vendors.length} vendors found
              </div>
            </div>

            {loading && (
              <div className="text-center py-12">
                <div className="text-2xl text-blue-600">Loading vendors...</div>
              </div>
            )}

            {!loading && vendors.length === 0 && (
              <div className="text-center py-12">
                <div className="text-xl text-gray-500 mb-4">No vendors found for {selectedCity}</div>
                <button 
                  onClick={() => setShowAddModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-xl font-bold text-white"
                >
                  Add First Vendor
                </button>
              </div>
            )}

            {!loading && vendors.length > 0 && (
              <div className="grid gap-4">
                {vendors.map((vendor) => (
                  <div key={vendor.id} className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-4 mb-3">
                          <h3 className="text-xl font-bold text-gray-800">{vendor.name}</h3>
                          <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                            {vendor.category}
                          </span>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <p className="text-gray-600">
                              <span className="font-semibold">📍 Address:</span> {vendor.address}
                            </p>
                            {(vendor.contact_name || vendor.phone) && (
                              <p className="text-gray-600 mt-1">
                                <span className="font-semibold">📞 Contact:</span> {vendor.contact_name || 'N/A'} | {vendor.phone || 'N/A'}
                              </p>
                            )}
                          </div>
                          <div>
                            <p className="text-gray-600">
                              <span className="font-semibold">📝 Notes:</span> {vendor.notes}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => handleEditVendor(vendor)}
                          className="bg-amber-600 hover:bg-amber-700 px-4 py-2 rounded-lg text-white font-bold text-sm shadow-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteVendor(vendor.id, vendor.name)}
                          className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg text-white font-bold text-sm shadow-sm"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Edit/Add Vendor Modal */}
        {(showEditModal || showAddModal) && (
          <VendorEditModal
            vendor={editingVendor}
            selectedCity={selectedCity}
            onClose={() => {
              setShowEditModal(false);
              setShowAddModal(false);
              setEditingVendor(null);
            }}
            onUpdate={() => handleCitySelect(selectedCity)}
          />
        )}

        {/* Location Manager Modal */}
        {showLocationManager && (
          <LocationManagerModal
            locations={locations}
            onClose={() => setShowLocationManager(false)}
            onUpdate={() => {
              fetchLocations();
              setShowLocationManager(false);
            }}
          />
        )}
        </div>
      </div>
  );
};

// Vendor Edit Modal Component
const VendorEditModal = ({ vendor, onClose, onUpdate, selectedCity }) => {
  const [formData, setFormData] = useState({
    name: vendor?.name || '',
    category: vendor?.category || '',
    city: selectedCity,
    address: vendor?.address || '',
    contact_name: vendor?.contact_name || '',
    phone: vendor?.phone || '',
    notes: vendor?.notes || ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (vendor?.id) {
        // Update existing vendor
        await axios.put(`${API}/vendors/${vendor.id}`, formData);
        alert('Vendor updated successfully!');
      } else {


        // Create new vendor
        await axios.post(`${API}/vendors`, formData);
        alert('Vendor created successfully!');
      }
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error saving vendor:', error);
      alert('Error saving vendor. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-12 sm:pt-4 overflow-y-auto">
      <div className="bg-white rounded-xl p-8 max-w-2xl w-full mx-4 shadow-2xl">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">
          {vendor?.id ? 'Edit Vendor' : 'Add New Vendor'}
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Company Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Category
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Select Category</option>
                <option value="Concrete Supply">Concrete Supply</option>
                <option value="Aggregate">Aggregate</option>
                <option value="Lumber Supply">Lumber Supply</option>
                <option value="Building Supply">Building Supply</option>
                <option value="Tool Supply">Tool Supply</option>
                <option value="Equipment Rental">Equipment Rental</option>
                <option value="Heat and Cooling">Heat and Cooling</option>
                <option value="Building Supply - Plumbing">Building Supply - Plumbing</option>
                <option value="Fuel Card">Fuel Card</option>
                <option value="Landfill">Landfill</option>
              </select>
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Address
            </label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* Contact Information */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Contact Name
              </label>
              <input
                type="text"
                value={formData.contact_name}
                onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Optional"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Phone Number
              </label>
              <input
                type="text"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Optional"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows="3"
              placeholder="Account information, PO requirements, etc."
              required
            />
          </div>

          <div className="flex gap-4 pt-4">
            <button 
              type="submit" 
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 py-3 rounded-lg font-bold text-white shadow-sm"
            >
              {loading ? 'Saving...' : (vendor?.id ? 'Update Vendor' : 'Add Vendor')}
            </button>
            <button 
              type="button" 
              onClick={onClose}
              className="flex-1 bg-gray-500 hover:bg-gray-600 py-3 rounded-lg font-bold text-white shadow-sm"
            >
              Cancel
            </button>
          </div>
        </form>
        </div>
      </div>
  );
};

// Simple Checklist Runner - Shows all items with Yes/No checkboxes

// LocationManagerModal
function LocationManagerModal({ locations, onClose, onUpdate }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    province: 'Ontario',
    region: ''
  });
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setFormData({
      name: '',
      province: 'Ontario',
      region: ''
    });
    setEditingLocation(null);
    setShowAddForm(false);
  };

  const handleEdit = (location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      province: location.province || 'Ontario',
      region: location.region || ''
    });
    setShowAddForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('Please enter a location name.');
      return;
    }

    setLoading(true);
    try {
      if (editingLocation) {
        // Update existing location
        await axios.put(`${API}/vendor-locations/${editingLocation.id}`, formData);
        alert(`Location "${formData.name}" updated successfully!`);
      } else {
        // Create new location
        await axios.post(`${API}/vendor-locations`, formData);
        alert(`Location "${formData.name}" created successfully!`);
      }
      resetForm();
      onUpdate();
    } catch (error) {
      console.error('Error saving location:', error);
      if (error.response?.status === 409) {
        alert(`Location "${formData.name}" already exists. Please choose a different name.`);
      } else {
        alert('Error saving location. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (location) => {
    if (window.confirm(`Are you sure you want to delete "${location.name}"? This will check if any vendors are assigned to this location.`)) {
      try {
        await axios.delete(`${API}/vendor-locations/${location.id}`);
        alert(`Location "${location.name}" deleted successfully!`);
        onUpdate();
      } catch (error) {
        console.error('Error deleting location:', error);
        if (error.response?.status === 409) {
          const response = error.response;
          const message = response.data?.detail || 'Cannot delete location - vendors are assigned to it';
          const shouldForceDelete = window.confirm(`${message}\n\nWould you like to force delete this location and remove all associated vendors?`);
          
          if (shouldForceDelete) {
            try {
              await axios.delete(`${API}/vendor-locations/${location.id}/force`);
              alert(`Location "${location.name}" and associated vendors deleted successfully!`);
              onUpdate();
            } catch (forceError) {
              console.error('Error force deleting location:', forceError);
              alert('Error force deleting location. Please try again.');
            }
          }
        } else {
          alert('Error deleting location. Please try again.');
        }
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-12 sm:pt-4 overflow-y-auto">
      <div className="bg-white rounded-xl p-8 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Manage Vendor Locations</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {/* Add/Edit Form */}
        {showAddForm && (
          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-bold mb-4 text-gray-700">
              {editingLocation ? 'Edit Location' : 'Add New Location'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Location Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="e.g., Atikokan"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Province
                  </label>
                  <select
                    value={formData.province}
                    onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                    className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="Ontario">Ontario</option>
                    <option value="Quebec">Quebec</option>
                    <option value="British Columbia">British Columbia</option>
                    <option value="Alberta">Alberta</option>
                    <option value="Manitoba">Manitoba</option>
                    <option value="Saskatchewan">Saskatchewan</option>
                    <option value="Nova Scotia">Nova Scotia</option>
                    <option value="New Brunswick">New Brunswick</option>
                    <option value="Newfoundland and Labrador">Newfoundland and Labrador</option>
                    <option value="Prince Edward Island">Prince Edward Island</option>
                    <option value="Northwest Territories">Northwest Territories</option>
                    <option value="Nunavut">Nunavut</option>
                    <option value="Yukon">Yukon</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Region
                  </label>
                  <input
                    type="text"
                    value={formData.region}
                    onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                    className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="e.g., Northwestern Ontario"
                  />
                </div>
              </div>
              <div className="flex gap-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 px-6 py-2 rounded-lg font-bold text-white"
                >
                  {loading ? 'Saving...' : (editingLocation ? 'Update' : 'Add Location')}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="bg-gray-500 hover:bg-gray-600 px-6 py-2 rounded-lg font-bold text-white"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Add New Location Button */}
        {!showAddForm && (
          <div className="mb-6">
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-green-600 hover:bg-green-700 px-6 py-3 rounded-lg font-bold text-white shadow-sm"
            >
              + Add New Location
            </button>
          </div>
        )}

        {/* Locations List */}
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-gray-700">Current Locations ({locations.length})</h3>
          
          {locations.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-4">🗺️</div>
              <p>No locations configured yet.</p>
              <p className="text-sm">Add your first location to get started.</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {locations.map((location) => (
                <div key={location.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="text-lg font-bold text-gray-800">{location.name}</h4>
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                          {location.province || 'Ontario'}
                        </span>
                      </div>
                      {location.region && (

                        <p className="text-gray-600 text-sm">
                          📍 {location.region}
                        </p>
                      )}
                      <p className="text-gray-500 text-xs mt-1">
                        Created: {new Date(location.created_date).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(location)}
                        className="bg-amber-600 hover:bg-amber-700 px-3 py-1 rounded-lg text-white font-bold text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(location)}
                        className="bg-red-600 hover:bg-red-700 px-3 py-1 rounded-lg text-white font-bold text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Close Button */}
        <div className="mt-8 text-center">
          <button
            onClick={onClose}
            className="bg-gray-600 hover:bg-gray-700 px-8 py-3 rounded-lg font-bold text-white"
          >
            Close Location Manager
          </button>
        </div>
        </div>
      </div>
  );
}

export default VendorsModule;
